/**
 * @fileoverview Defines authentication error types and corresponding user-friendly messages
 * for the Kanopi authentication system.
 *
 * This file contains:
 * - An enum of all possible authentication error types
 * - A subset of user-facing error types
 * - Corresponding error messages for user-facing errors
 */

/**
 * Enum representing all possible authentication errors that can occur.
 * Errors are categorized into internal server errors and user-facing errors.
 * The kanopi_token_auth mutation from our Django backend defines these errors.
 */
export enum KanopiAuthErrorType {
  // Internal Server Type Errors
  INVALID_JWT = "INVALID_JWT",
  MISSING_KID = "MISSING_KID",
  JWKS_FETCH_ERROR = "JWKS_FETCH_ERROR",
  UNSUPPORTED_KEY_TYPE = "UNSUPPORTED_KEY_TYPE",
  KEY_NOT_FOUND = "KEY_NOT_FOUND",
  INVALID_TOKEN = "INVALID_TOKEN",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",

  // General Usability Errors that require user feedback
  NO_EMAIL = "NO_EMAIL",
  NO_ORG_SLUG = "NO_ORG_SLUG",
  USER_NOT_FOUND = "USER_NOT_FOUND",
  EXPIRED_TOKEN = "EXPIRED_TOKEN",
  ORGANIZATION_MISMATCH = "ORGANIZATION_MISMATCH",
}

/**
 * Type defining the subset of authentication errors that require
 * user-friendly messages to be displayed in the UI.
 */
type SupportedAuthErrorType = Pick<
  typeof KanopiAuthErrorType,
  | "NO_EMAIL"
  | "NO_ORG_SLUG"
  | "USER_NOT_FOUND"
  | "EXPIRED_TOKEN"
  | "ORGANIZATION_MISMATCH"
>;

/**
 * Maps user-facing authentication error types to human-readable messages.
 * These messages are displayed to users when authentication fails.
 *
 * Only includes messages for errors defined in SupportedAuthErrorType.
 * Internal server errors are handled separately and not exposed to users.
 */
export const KanopiAuthErrorMessages: Record<keyof SupportedAuthErrorType, string> = {
  [KanopiAuthErrorType.NO_EMAIL]:
    "The users email address is missing from the request.",
  [KanopiAuthErrorType.NO_ORG_SLUG]:
    "The users organisation slug is missing from the request.",
  [KanopiAuthErrorType.USER_NOT_FOUND]: "The user was not found in the system.",
  [KanopiAuthErrorType.EXPIRED_TOKEN]: "The authentication request has expired.",
  [KanopiAuthErrorType.ORGANIZATION_MISMATCH]:
    "You're trying to access an organization that doesn't match your request.",
};
